import { Suspense, lazy, useCallback, useEffect, useState } from "react";

import Header from "./components/Header";
import Preloader from "./components/Preloader";
import ScrollProgress from "./components/ScrollProgress";
import Courses from "./sections/Courses";
import Cta from "./sections/Cta";
import Footer from "./sections/Footer";
import Hero from "./sections/Hero";
import Proud from "./sections/Proud";
import Purpose from "./sections/Purpose";
import { supportsWebGL } from "./lib/capabilities";
import { ScrollTrigger } from "./lib/motion";
import { useSmoothScroll } from "./lib/useSmoothScroll";

/* three + drei live in their own chunk, fetched only once the page is idle and
   only where WebGL exists. The flat SVG icons cover everyone else. */
const IconStage = lazy(() => import("./three/IconStage"));

export default function App() {
  const lenisRef = useSmoothScroll();
  const [ready, setReady] = useState(false);
  const [stage3D, setStage3D] = useState(false);

  const onLoaderComplete = useCallback(() => setReady(true), []);

  /* The 3D stage (three + drei, the app's heaviest chunk) loads on the first
     sign the user is coming: the first icon section entering the viewport, or
     any input at all — whichever happens first. Every icon lives below the
     fold, so nothing is missed; the old idle fetch was paying ~3s of mobile
     script evaluation during initial load for solids that weren't on screen.
     No rootMargin on the observer: tall phone/tablet viewports sit within a
     few hundred px of the section, and any margin would re-trigger the load
     at parse time and defeat the deferral. Input covers the prefetch headroom
     a margin would have bought. */
  useEffect(() => {
    if (!supportsWebGL()) return undefined;

    const cleanups = [];
    let armed = false;
    const arm = () => {
      if (armed) return;
      armed = true;
      cleanups.forEach((fn) => fn());
      setStage3D(true);
    };

    const target = document.querySelector(".ilab-purpose");
    if (!target || typeof IntersectionObserver === "undefined") {
      arm();
      return undefined;
    }

    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) arm();
    });
    io.observe(target);
    cleanups.push(() => io.disconnect());

    ["wheel", "touchstart", "keydown", "pointerdown"].forEach((type) => {
      window.addEventListener(type, arm, { passive: true, once: true });
      cleanups.push(() => window.removeEventListener(type, arm));
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);

  /* Every section measures differently once the webfonts land and the loader
     curtain has cleared, so re-measure the triggers at both moments. */
  useEffect(() => {
    if (!ready) return undefined;
    const id = window.setTimeout(() => ScrollTrigger.refresh(), 120);
    document.fonts?.ready.then(() => ScrollTrigger.refresh());
    return () => window.clearTimeout(id);
  }, [ready]);

  return (
    <>
      <a className="skip-link" href="#content">
        Skip to content
      </a>

      <Preloader onComplete={onLoaderComplete} />
      <Header lenisRef={lenisRef} ready={ready} />

      <main id="content">
        <Hero ready={ready} />
        <Purpose />
        <Courses />
        <Proud />
        <Cta lenisRef={lenisRef} />
      </main>

      <Footer />
      <ScrollProgress lenisRef={lenisRef} />

      {stage3D ? (
        <Suspense fallback={null}>
          <IconStage />
        </Suspense>
      ) : null}
    </>
  );
}
